// src/components/popups/WithdrawDrawer.js
import React, { useMemo, useState } from 'react';
import './WithdrawDrawer.css';
import { useTranslation } from 'react-i18next';

function WithdrawDrawer({ onClose, onWithdraw, minAmount = 10, maxAmount }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');

  // ✅ iOS locale fix: allow comma input, normalize to dot for JS Number()
  const normalizedAmount = useMemo(() => {
    if (!amount) return '';
    return String(amount).replace(',', '.');
  }, [amount]);

  const numericAmount = useMemo(() => Number(normalizedAmount), [normalizedAmount]);

  const amountInvalid = !amount || !Number.isFinite(numericAmount) || numericAmount <= 0;
  const amountTooLow =
    Number.isFinite(numericAmount) &&
    numericAmount > 0 &&
    numericAmount < Number(minAmount);

  const addressInvalid = !walletAddress || walletAddress.trim().length < 10;

  const disableSend = amountInvalid || amountTooLow || addressInvalid;

  const handleWithdraw = () => {
    if (amountInvalid) {
      alert(t('withdraw.errors.invalid_amount'));
      return;
    }
    if (numericAmount < Number(minAmount)) {
      alert(t('withdraw.errors.min', { min: minAmount }));
      return;
    }
    if (addressInvalid) {
      alert(t('withdraw.errors.invalid_address'));
      return;
    }

    onWithdraw({
      amount: numericAmount,
      walletAddress: walletAddress.trim(),
    });
    onClose();
  };

  return (
    <div className="withdraw-drawer-backdrop" onClick={onClose}>
      <div className="withdraw-drawer-content" onClick={(e) => e.stopPropagation()}>
        <div className="withdraw-header">
          <span>{t('withdraw.title')}</span>
          <button className="withdraw-close" onClick={onClose} type="button">
            ✕
          </button>
        </div>

        <input
          // ✅ IMPORTANT: use text so iOS doesn't block comma/dot input
          type="text"
          className="withdraw-input"
          placeholder={t('withdraw.amount_placeholder', {
            min: minAmount,
            max: typeof maxAmount === 'number' ? maxAmount : undefined,
          })}
          value={amount}
          inputMode="decimal"
          step="any"
          min={minAmount}
          onChange={(e) => {
            // ✅ keep only digits + one decimal separator (comma OR dot)
            let v = e.target.value || '';
            v = v.replace(/[^0-9.,]/g, '');

            // If user types both "," and ".", keep the first separator only
            const firstComma = v.indexOf(',');
            const firstDot = v.indexOf('.');
            const firstSep = (() => {
              if (firstComma === -1) return firstDot;
              if (firstDot === -1) return firstComma;
              return Math.min(firstComma, firstDot);
            })();

            if (firstSep !== -1) {
              const before = v.slice(0, firstSep + 1);
              const after = v.slice(firstSep + 1).replace(/[.,]/g, '');
              v = before + after;
            }

            setAmount(v);
          }}
        />

        {amountTooLow && (
          <div
            className="withdraw-hint"
            style={{ color: '#d9534f', fontSize: 12, marginTop: -6, marginBottom: 6 }}
          >
            {t('withdraw.errors.min', { min: minAmount })}
          </div>
        )}

        <input
          type="text"
          className="withdraw-input"
          placeholder={t('withdraw.address_placeholder')}
          value={walletAddress}
          onChange={(e) => setWalletAddress(e.target.value)}
        />

        <button
          className="withdraw-send-btn"
          onClick={handleWithdraw}
          disabled={disableSend}
          type="button"
        >
          {t('withdraw.submit')}
        </button>
      </div>
    </div>
  );
}

export default WithdrawDrawer;
